import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, BookOpen, UploadCloud, FileCheck2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const YEARS = ["1st Year", "2nd Year", "3rd Year", "4th Year", "5th Year", "Employee"];
const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"];
const FOOD_PREFS = ["Vegetarian", "Non-Vegetarian"];

const TERMS = [
  "I have read and agree to the rules and regulations of Vasavi Boys Hostel.",
  "In the event of cancellation under any circumstances, the fee shall not be refundable.",
  "The above details are accurate and true to the best of my knowledge. I take full responsibility for any errors or misleading information contained therein.",
];

const EMPTY = {
  surname: "",
  name: "",
  contact_number: "",
  kl_id: "",
  date_of_birth: "",
  parent_full_name: "",
  parent_contact_number: "",
  year: "",
  branch: "",
  blood_group: "",
  food_preference: "",
  residential_address: "",
  email: "",
  password: "",
  photo_path: "",
  aadhar_path: "",
  university_id_path: "",
};

export default function Register() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [terms, setTerms] = useState([false, false, false]);
  const [uploading, setUploading] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [message, setMessage] = useState("");

  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const allTerms = terms.every(Boolean);

  const uploadFile = async (field, file) => {
    setUploading((u) => ({ ...u, [field]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/register/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      change(field, data.path);
      toast.success("File uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading((u) => ({ ...u, [field]: false }));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    // Validate required select-based / file fields (browser can't enforce these)
    const missing = [];
    // Trim text values so whitespace-only doesn't pass HTML required
    const textFields = [
      ["surname", "Surname"],
      ["name", "Student / Employee Name"],
      ["contact_number", "Student / Employee Contact"],
      ["kl_id", "KL ID / Admission No."],
      ["parent_full_name", "Parent Full Name"],
      ["parent_contact_number", "Parent Contact"],
      ["branch", "Branch"],
      ["residential_address", "Residential Address"],
    ];
    for (const [k, label] of textFields) {
      if (!String(form[k] ?? "").trim()) missing.push(label);
    }
    if (!form.date_of_birth) missing.push("Date of Birth");
    if (!form.blood_group) missing.push("Blood Group");
    if (!form.year) missing.push("Year");
    if (!form.food_preference) missing.push("Food Preference");
    if (!form.aadhar_path) missing.push("Aadhar upload");
    if (!form.photo_path) missing.push("Passport-size Photo");
    if (!form.university_id_path) missing.push("University / Employee ID");
    if (missing.length) {
      const msg = `Please fill: ${missing.join(", ")}`;
      setError(msg);
      toast.error(msg);
      return;
    }
    if (!allTerms) {
      const msg = "Please accept all 3 terms & conditions before submitting";
      setError(msg);
      toast.error(msg);
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, terms_accepted: true };
      const { data } = await api.post("/auth/register", payload);
      toast.success("Enrollment submitted");
      setSubmitted(true);
      setMessage(
        data?.message ||
          "Your account is pending admin approval. You'll be able to log in once approved.",
      );
    } catch (err) {
      const msg = err.response?.data?.detail || "Registration failed";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
      toast.error(typeof msg === "string" ? msg : "Registration failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="register-page">
      <div className="px-6 py-6 flex items-center justify-between max-w-5xl mx-auto">
        <Link
          to="/"
          className="inline-flex items-center gap-2 font-bold text-sm px-4 py-2 border-2 border-[#0A0A0A] rounded-full bg-white hover:shadow-brutal-sm transition-shadow"
        >
          <ArrowLeft size={16} /> Home
        </Link>
        <Link
          to="/rules"
          data-testid="rules-button"
          className="inline-flex items-center gap-2 font-bold text-sm px-4 py-2 border-2 border-[#0A0A0A] rounded-full bg-[#00E5FF] shadow-brutal-sm hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-transform"
        >
          <BookOpen size={16} /> Hostel Rules
        </Link>
      </div>

      <div className="max-w-5xl mx-auto px-6 pb-16">
        {submitted ? (
          <div
            className="brutal-card p-8 md:p-12 bg-white text-center"
            data-testid="register-success"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1 border-2 border-[#0A0A0A] rounded-full bg-[#FFE600] text-xs font-bold uppercase tracking-[0.2em]">
              Request received
            </div>
            <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter mt-4">
              Waiting for admin approval
            </h1>
            <p className="mt-4 font-medium text-base max-w-xl mx-auto">{message}</p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 font-bold px-6 py-3 border-2 border-[#0A0A0A] rounded-full bg-[#FFE600] shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform"
              >
                Back to Home
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 font-bold px-6 py-3 border-2 border-[#0A0A0A] rounded-full bg-white shadow-brutal-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform"
              >
                Go to Login
              </Link>
            </div>
          </div>
        ) : (
        <>
        <div className="mb-8">
          <span className="text-xs font-bold uppercase tracking-[0.2em]">Get started</span>
          <h1 className="font-heading font-black text-4xl md:text-5xl mt-2 tracking-tighter">
            New enrollment
          </h1>
          <p className="mt-2 text-sm font-medium text-[#4A4A4A] max-w-xl">
            Fill in your details to enroll at Vasavi Boys Hostel. Fields marked <span className="text-[#FF4D4D] font-bold">*</span> are required.
            After submission, your account will be reviewed and approved by the hostel admin.
          </p>
        </div>

        <form onSubmit={submit} className="brutal-card p-6 md:p-8 bg-white" data-testid="register-form">
          <Section title="Personal details">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Surname" required>
                <input data-testid="reg-surname" value={form.surname} required
                  onChange={(e) => change("surname", e.target.value)} className="input-brutal" />
              </Field>
              <Field label="Student / Employee Name" required>
                <input data-testid="reg-name" value={form.name} required
                  onChange={(e) => change("name", e.target.value)} className="input-brutal" />
              </Field>
              <Field label="Student / Employee Contact Number" required>
                <input data-testid="reg-contact" value={form.contact_number} required
                  onChange={(e) => change("contact_number", e.target.value)} className="input-brutal" />
              </Field>
              <Field label="KL ID / Admission No. / Employee ID" required>
                <input data-testid="reg-kl-id" value={form.kl_id} required
                  onChange={(e) => change("kl_id", e.target.value)} className="input-brutal" />
              </Field>
              <Field label="Date of Birth" required>
                <input data-testid="reg-dob" type="date" value={form.date_of_birth} required
                  onChange={(e) => change("date_of_birth", e.target.value)} className="input-brutal" />
              </Field>
              <Field label="Blood Group" required>
                <Select value={form.blood_group} onValueChange={(v) => change("blood_group", v)}>
                  <SelectTrigger data-testid="reg-blood" className="input-brutal font-bold">
                    <SelectValue placeholder="Select blood group" />
                  </SelectTrigger>
                  <SelectContent className="border-2 border-[#0A0A0A]">
                    {BLOOD_GROUPS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </Section>

          <Section title="Parent details">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Parent Full Name" required>
                <input data-testid="reg-parent-name" value={form.parent_full_name} required
                  onChange={(e) => change("parent_full_name", e.target.value)} className="input-brutal" />
              </Field>
              <Field label="Parent Contact Number" required>
                <input data-testid="reg-parent-contact" value={form.parent_contact_number} required
                  onChange={(e) => change("parent_contact_number", e.target.value)} className="input-brutal" />
              </Field>
            </div>
          </Section>

          <Section title="Stay details">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Year" required>
                <Select value={form.year} onValueChange={(v) => change("year", v)}>
                  <SelectTrigger data-testid="reg-year" className="input-brutal font-bold">
                    <SelectValue placeholder="Select year" />
                  </SelectTrigger>
                  <SelectContent className="border-2 border-[#0A0A0A]">
                    {YEARS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Branch" required>
                <input data-testid="reg-branch" value={form.branch} required
                  onChange={(e) => change("branch", e.target.value)} className="input-brutal"
                  placeholder="e.g. CSE, ECE, MBA" />
              </Field>
              <Field label="Food Preference" required>
                <Select value={form.food_preference} onValueChange={(v) => change("food_preference", v)}>
                  <SelectTrigger data-testid="reg-food" className="input-brutal font-bold">
                    <SelectValue placeholder="Select food preference" />
                  </SelectTrigger>
                  <SelectContent className="border-2 border-[#0A0A0A]">
                    {FOOD_PREFS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Residential Address" required full>
                <textarea data-testid="reg-address" value={form.residential_address} required
                  onChange={(e) => change("residential_address", e.target.value)}
                  rows={2} className="input-brutal" />
              </Field>
            </div>
            <p className="mt-3 text-xs font-medium text-[#4A4A4A]">
              Room number, sub-room, and bed-sharing will be assigned by the hostel admin after your account is approved.
            </p>
          </Section>

          <Section title="Documents">
            <p className="text-xs font-bold text-[#4A4A4A] -mt-2 mb-3">
              All three uploads are required <span className="text-[#FF4D4D]">*</span>
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FileUploader
                label="Aadhar (photo or PDF)"
                required
                value={form.aadhar_path}
                uploading={uploading.aadhar_path}
                onFile={(f) => uploadFile("aadhar_path", f)}
                testId="reg-aadhar"
              />
              <FileUploader
                label="Passport-size Photo"
                required
                value={form.photo_path}
                uploading={uploading.photo_path}
                onFile={(f) => uploadFile("photo_path", f)}
                testId="reg-photo"
                accept="image/*"
              />
              <FileUploader
                label="University / Employee ID"
                required
                value={form.university_id_path}
                uploading={uploading.university_id_path}
                onFile={(f) => uploadFile("university_id_path", f)}
                testId="reg-uni-id"
              />
            </div>
          </Section>

          <Section title="Login details">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Email" required>
                <input data-testid="reg-email" type="email" value={form.email} required
                  onChange={(e) => change("email", e.target.value)} className="input-brutal" />
              </Field>
              <Field label="Password" required>
                <input data-testid="reg-password" type="password" value={form.password}
                  minLength={6} required
                  onChange={(e) => change("password", e.target.value)} className="input-brutal"
                  placeholder="Minimum 6 characters" />
              </Field>
            </div>
          </Section>

          <Section title={<>Terms &amp; Conditions <span className="text-[#FF4D4D]">*</span></>}>
            <div className="space-y-3" data-testid="reg-terms">
              {TERMS.map((t, idx) => (
                <label
                  key={idx}
                  className="flex items-start gap-3 p-3 border-2 border-[#0A0A0A] rounded-xl bg-[#FFFCE0] cursor-pointer"
                >
                  <Checkbox
                    data-testid={`reg-terms-${idx}`}
                    checked={terms[idx]}
                    onCheckedChange={(v) => {
                      const next = [...terms];
                      next[idx] = !!v;
                      setTerms(next);
                    }}
                    className="mt-0.5 border-2 border-[#0A0A0A] data-[state=checked]:bg-[#0A0A0A]"
                  />
                  <span className="text-sm font-medium leading-snug">
                    <span className="font-bold mr-1">{idx + 1}.</span>
                    {t}
                  </span>
                </label>
              ))}
              <p className="text-xs font-medium text-[#4A4A4A]">
                Full hostel rules —{" "}
                <Link to="/rules" className="font-bold underline underline-offset-4">
                  read all rules
                </Link>
                .
              </p>
            </div>
          </Section>

          {error && (
            <div className="mt-4 bg-[#FF4D4D] text-white border-2 border-[#0A0A0A] rounded-xl px-4 py-2 text-sm font-bold">
              {error}
            </div>
          )}

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm font-medium text-[#4A4A4A]">
              Already have an account?{" "}
              <Link to="/login" className="font-bold underline underline-offset-4">
                Log in
              </Link>
            </p>
            <button
              type="submit"
              disabled={saving}
              data-testid="reg-submit"
              className="inline-flex items-center gap-2 font-bold px-6 py-3 border-2 border-[#0A0A0A] rounded-full bg-[#FFE600] shadow-brutal hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-transform disabled:opacity-60"
            >
              {saving ? "Submitting..." : "Submit for approval"}
            </button>
          </div>
        </form>
        </>
        )}
      </div>

      <style>{`
        .input-brutal {
          width: 100%;
          padding: 0.7rem 1rem;
          border: 2px solid #0A0A0A;
          border-radius: 0.75rem;
          background: white;
          font-weight: 500;
        }
        .input-brutal:focus {
          outline: none;
          box-shadow: 0 0 0 2px white, 0 0 0 4px #0A0A0A;
        }
      `}</style>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-2 mb-6">
      <h3 className="font-heading font-black text-lg tracking-tight uppercase mb-3">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, required, full, children }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="block text-xs font-bold uppercase tracking-widest mb-1.5">
        {label} {required && <span className="text-[#FF4D4D]">*</span>}
      </span>
      {children}
    </label>
  );
}

function FileUploader({ label, value, uploading, onFile, testId, accept, required }) {
  return (
    <div className="border-2 border-[#0A0A0A] rounded-xl bg-[#FFFCE0] p-4">
      <div className="text-xs font-bold uppercase tracking-widest mb-2">
        {label} {required && <span className="text-[#FF4D4D]">*</span>}
      </div>
      {value ? (
        <div className="flex items-center gap-2 text-xs font-bold bg-[#00CC66] text-white px-2 py-1 border-2 border-[#0A0A0A] rounded mb-2 w-fit">
          <FileCheck2 size={14} strokeWidth={2.5} /> Uploaded
        </div>
      ) : null}
      <label className="inline-flex items-center gap-2 font-bold text-sm px-3 py-2 border-2 border-[#0A0A0A] rounded-full bg-white cursor-pointer hover:shadow-brutal-sm transition-shadow">
        <UploadCloud size={14} strokeWidth={2.5} />
        {uploading ? "Uploading..." : value ? "Replace" : "Upload"}
        <input
          type="file"
          accept={accept || "image/*,application/pdf"}
          onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])}
          className="hidden"
          data-testid={testId}
        />
      </label>
    </div>
  );
}
